import React, { useState, useEffect, useRef } from 'react';
import { LogOut } from 'lucide-react';
import { isAdmin } from '../utils.js';
import { VERSION } from '../version.js';
import { I } from '../icons.jsx';
import { useTheme } from '../contexts/theme-context.js';
import { useMobile, useViewportWidth } from '../hooks/use-mobile.js';
import { SyncStatus } from './SyncStatus.jsx';
import RefreshButton from './RefreshButton.jsx';
import { NavDropdown } from './NavDropdown.jsx';
import { MobileNav } from './MobileNav.jsx';
import { onPresenceUpdate } from '../lib/api-client.js';

/* OnlineCounter: зелёный пульсирующий чип с поповером «Active now».
   Desktop — открывается по hover (CSS :hover). Touch/mobile — по тапу:
   onClick тоглит open-состояние (CSS .blitz-online--open), закрытие — тапом
   мимо (document pointerdown вне ref).
   Оптимистично показывает минимум себя (count 1) сразу на маунте — не ждёт
   сервер; реальное число приходит по WS (+ кэш-replay при ремаунте шапки). */
export function OnlineCounter({ currentName }) {
  const [p, setP] = useState(() => ({ count: currentName ? 1 : 0, users: currentName ? [currentName] : [] }));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => onPresenceUpdate(setP), []);
  useEffect(() => {
    if (!open) return undefined;
    const onAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onAway);
    return () => document.removeEventListener('pointerdown', onAway);
  }, [open]);
  // F1-follow-up (2026-06-13): ALWAYS render — never `return null` on count 0. The presence
  // tracker drops count→0 when the tab is blurred/idle and restores it on refocus; unmounting
  // the chip made the whole header reflow ("jerk") on every window switch. Always-mounted +
  // fixed number width = zero layout shift. At 0 the dot is muted (grey, no pulse) since a green
  // "active" pulse showing 0 would be misleading.
  return (
    <span
      ref={ref}
      className={open ? "blitz-online blitz-online--open" : "blitz-online"}
      aria-label={`${p.count} active`}
      onClick={() => setOpen((o) => !o)}
    >
      <span className="blitz-online-dot" style={p.count ? undefined : { background: "#94A3B8", boxShadow: "none", animation: "none" }} />
      <span style={{ minWidth: "2.2ch", textAlign: "center", display: "inline-block" }}>{p.count}</span>
      <span className="blitz-online-pop">
        <span className="blitz-online-pop-h">Active now</span>
        {p.users.map((n, i) => (
          <span className="blitz-online-row" key={i}>
            <span className="blitz-online-pd" />{n}
            {n === currentName && <span className="blitz-online-you">you</span>}
          </span>
        ))}
      </span>
    </span>
  );
}

/* Shared Responsive Header -- extracted verbatim from App.jsx (C1 refactor,
   behavior-preserving). Composes NavDropdown (desktop) + MobileNav (mobile) +
   SyncStatus + RefreshButton.

   F1-v2 FIX (2026-06-13): Corrected three-tier responsive layout.
   Measurements showed two clipping bands in v1: compact clipped at 1024–1199
   and full clipped at 1280–1545 (Offline SyncStatus +57px wide). Root cause:
   nav-links at 606px dominated LEFT group; full tier at 1280 was too low.

   Fix: compress nav to ~480px (11px font, 4/6px pad, 3px gap) in compact mode
   + raise full-tier threshold to 1440 (well above ~1376px worst-case content).

   F1-v3 FIX (2026-06-13): Two remaining clipping bands eliminated structurally.
   Root cause: SyncStatus width varies 78px (Live) → 135px+ (Offline+N pending),
   making pixel budgets unreliable. version badge ~70px also wasted in compact.

   Structural changes (not budget calculations):
   1. Raise compact threshold 1440→1560: converts the 1440–1559 full-clips band
      to compact (which fits at those widths).
   2. Hide v{VERSION} badge in compact mode: ~70px deterministically recovered.
   3. SyncStatus compact prop: glyph-only ~28px in Tier B, eliminates ~105px
      worst-case Offline swing. Full pill in Tier A and mobile.

   F1-v4 TWEAK (2026-06-13): Owner-requested polish — icon-only controls + version always visible.
   1. v{VERSION} badge now visible at ALL desktop widths (full + compact). Removing
      the compact-hide saves ~45px (Refresh label gone) and the version adds ~70px, net +25px.
   2. Revert button: icon-only (↩) at ALL widths — no text label; consistent padding 6px 8px.
   3. Refresh button (RefreshButton.jsx): icon-only (🔄) at all widths — no text label.

   Layout tiers are JS-driven (gated on viewport width) — not CSS @media queries.

   Tier A — full desktop     (>= 1560px): all controls + name badge + version visible.
   Tier B — compact desktop  (1024–1559px): name badge hidden, version badge visible,
                              Revert/Refresh/Logout icon-only, compressed nav (11px, 4/6px pad,
                              3px gap), SyncStatus glyph-only (~28px fixed width).
                              Below 1200px also hides "Blitz CRM" wordmark.
   Tier C — hamburger        (<= 1023px):  MobileNav. Breakpoint <=1023 keeps
                              JSDOM default innerWidth=1024 in desktop-mode for tests.

   Width budget (compact, WIDE SyncStatus "Offline", wordmark hidden at <1200):
     Left:  logo(28) + version(70) + online(38) + divider(12) + nav(~480) + gaps(~70) ≈ 698px
     Right: admin(38) + refresh(38) + revert(38) + SyncStatus(135) + logout(38) + gaps(~30) ≈ 317px
     Total ≈ 1015px  →  fits 1024 with ~9px margin; fits 1100 with 85px margin ✓

   Width budget (compact, WIDE SyncStatus, wordmark shown at 1200–1559):
     Left adds wordmark (~80px) → total ≈ 1095px  →  fits 1200 with 105px margin ✓

   Width budget (full, >= 1560, WIDE SyncStatus):
     Left:  logo(28) + wordmark(80) + version(70) + online(38) + divider(12) + nav(~480) + gaps(~85) ≈ 793px
     Right: admin(95) + name(110) + refresh(38) + revert(38) + SyncStatus(135) + logout(38) + gaps(~50) ≈ 504px
     Total ≈ 1297px  →  fits 1560 with 263px margin ✓

   Key: nav compressed (compact) = 4 dropdowns × avg ~88px + 2 singles × avg ~70px
        + 5 item-gaps × 2px = ~494px; conservative budget uses ~480px.
        Full-mode nav = 4×~105px + 2×~85px + 5×4px = 590px (was 606px in v1, same font/pad).
*/
export function BlitzHeader({ user, activePage, userAccess, onNav, onAdmin, onLogout, accentColor }) {
  const mobile = useMobile();
  const vw = useViewportWidth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { dark, toggle: toggleDark } = useTheme();

  // Layout tier derived from viewport width (JS-driven, no CSS @media needed)
  // Tier A: full desktop (>= 1560); Tier B: compact desktop (1024–1559); Tier C: hamburger (<= 1023 via useMobile)
  // F1-v3: raised 1440 → 1560 (measured full content ~1516px worst-case Offline SyncStatus).
  const compact = !mobile && vw < 1560;      // Tier B — F1-v3: raised 1440→1560 (full content ~1516px worst-case Offline)
  // Within compact, hide wordmark below 1200 for ~80px extra breathing room
  const hideWordmark = compact && vw < 1200;

  // v9.05: Grouped navigation structure
  const NAV_GROUPS = [
    { label: "Dashboard", icon: "📊", type: "single", key: "overview", color: "#6366F1" },
    { label: "Finance", icon: "💰", type: "dropdown", color: "#0EA5E9", items: [
      { key: "payments", label: "Payments", color: "#0EA5E9" },
      { key: "customers", label: "Customer Payments", color: "#0EA5E9" },
      { key: "dailycalcs", label: "Daily Calcs", color: "#EF4444" },
      { key: "genbalances", label: "Gen Balances", color: "#10B981" },
      { key: "invoicegen", label: "Invoice Generator", color: "#F97316" },
    ]},
    { label: "Deals", icon: "📋", type: "dropdown", color: "#F59E0B", items: [
      { key: "crg", label: "CRG Deals", color: "#F59E0B" },
      { key: "deals", label: "Offers", color: "#10B981" },
      { key: "dailycap", label: "Daily Cap", color: "#8B5CF6" },
    ]},
    { label: "Info", icon: "📊", type: "dropdown", color: "#EC4899", items: [
      { key: "partners", label: "Partners", color: "#EC4899" },
      { key: "roster", label: "Affiliates & Brokers", color: "#14B8A6" },
      { key: "brokers", label: "Brokers", color: "#52606d" },
      { key: "phonecheck", label: "Phone Checker", color: "#2563EB" },
      { key: "monthlystats", label: "Blitz Report", color: "#6366F1" },
      { key: "ftdsinfo", label: "FTDs Info", color: "#10B981" },
      { key: "ai", label: "🤖 AI Advisor", color: "#A855F7" },
    ]},
    { label: "AI", icon: "🧠", type: "dropdown", color: "#A855F7", items: [
      // v32.16.65 (2026-06-10): Analytics entry removed — вкладка убрана из CRM (page dormant в src/pages/Analytics/).
      { key: "autob2026", label: "AUTO-B2026", color: "#A855F7" },
      { key: "injections", label: "Injections", color: "#10B981" },
      // Radar (наблюдатель партнёрских чатов) перенесён из топ-навбара сюда — последним в AI Space.
      { key: "radar", label: "Radar", color: "#06B6D4" },
    ]},
    { label: "Settings", icon: "⚙️", type: "single", key: "settings", color: "#64748B" },
  ];

  // Flat list for mobile + admin
  const allNavPages = [
    { key: "overview", label: "Dashboard", color: "#6366F1" },
    { key: "radar", label: "🛰️ Radar", color: "#06B6D4" },
    { key: "payments", label: "Payments", color: "#0EA5E9" },
    { key: "customers", label: "Customer Payments", color: "#0EA5E9" },
    { key: "crg", label: "CRG Deals", color: "#F59E0B" },
    { key: "dailycap", label: "Daily Cap", color: "#8B5CF6" },
    { key: "deals", label: "Offers", color: "#10B981" },
    { key: "partners", label: "Partners", color: "#EC4899" },
    { key: "roster", label: "Affiliates & Brokers", color: "#14B8A6" },
    { key: "brokers", label: "Brokers", color: "#52606d" },
    { key: "phonecheck", label: "Phone Checker", color: "#2563EB" },
    { key: "monthlystats", label: "Blitz Report", color: "#6366F1" },
    { key: "ftdsinfo", label: "FTDs Info", color: "#10B981" },
    { key: "ai", label: "🤖 AI Advisor", color: "#A855F7" },
    { key: "dailycalcs", label: "Daily Calcs", color: "#EF4444" },
    { key: "genbalances", label: "Gen Balances", color: "#10B981" },
    { key: "invoicegen", label: "Invoice Generator", color: "#F97316" },
    { key: "autob2026", label: "AUTO-B2026", color: "#A855F7" },
    { key: "injections", label: "Injections", color: "#10B981" },
    { key: "settings", label: "Settings", color: "#64748B" },
  ];
  if (isAdmin(user.email)) allNavPages.push({ key: "admin", label: "⚙️ Admin", color: "#DC2626" });

  // F1-v2: compact nav style — 11px font, 4/6px pad, 3px icon-gap (vs 13px/5/12px/5px full)
  const navFont = compact ? 11 : 13;
  const navPad = compact ? "4px 6px" : "5px 12px";
  const navIconGap = compact ? 3 : 5;
  const navIconSize = compact ? 12 : 14;

  return (
    <>
      <header className="blitz-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 32px", borderBottom: "1px solid rgba(99,102,241,0.2)", background: "rgba(10,15,30,0.97)", backdropFilter: "blur(24px) saturate(200%)", WebkitBackdropFilter: "blur(24px) saturate(200%)", position: "sticky", top: "var(--offline-banner-h, 0px)", zIndex: 100, boxShadow: "0 1px 0 rgba(99,102,241,0.25), 0 4px 32px rgba(0,0,0,0.3)" }}>
        {/* LEFT GROUP: logo · wordmark · version · online-counter · divider · nav-links */}
        <div style={{ display: "flex", alignItems: "center", gap: mobile ? 8 : compact ? 6 : 12 }}>
          {I.logo}
          {/* F1-v2: hide wordmark below 1200px in compact mode to reclaim ~80px */}
          {!hideWordmark && (
            <span className={mobile ? "blitz-logo blitz-logo--mobile" : "blitz-logo"} data-text="Blitz CRM">Blitz CRM</span>
          )}
          {/* version badge visible at ALL desktop widths (full + compact); hidden only in mobile/hamburger */}
          {!mobile && <span style={{ fontSize: 11, color: "#A5B4FC", fontFamily: "'JetBrains Mono',monospace", background: "rgba(99,102,241,0.15)", padding: "3px 10px", borderRadius: 20, border: "1px solid rgba(99,102,241,0.35)", fontWeight: 700, letterSpacing: "0.3px" }}>v{VERSION}</span>}
          {/* OnlineCounter preserved in all desktop tiers (full + compact) */}
          <OnlineCounter currentName={user?.name} />
          {!mobile && <>
            <span style={{ color: "rgba(99,102,241,0.3)", margin: "0 2px" }}>|</span>
            <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: compact ? 1 : 4 }}>
              {NAV_GROUPS.map(group => {
                if (group.type === "single") {
                  if (!(userAccess || []).includes(group.key)) return null;
                  const isActive = activePage === group.key;
                  return isActive
                    ? <span key={group.key} style={{ display: "flex", alignItems: "center", gap: navIconGap, background: `${group.color}15`, border: `1.5px solid ${group.color}40`, color: group.color, padding: navPad, borderRadius: 8, fontSize: navFont, fontWeight: 700 }}><span style={{ fontSize: navIconSize }}>{group.icon}</span>{group.label}</span>
                    : <button key={group.key} onClick={() => onNav(group.key)} style={{ display: "flex", alignItems: "center", gap: navIconGap, background: "transparent", border: "1.5px solid transparent", color: "#94A3B8", cursor: "pointer", fontSize: navFont, fontWeight: 700, padding: navPad, borderRadius: 8, transition: "all 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.color = group.color; e.currentTarget.style.background = "rgba(99,102,241,0.12)"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#94A3B8"; e.currentTarget.style.background = "transparent"; }}
                      ><span style={{ fontSize: navIconSize }}>{group.icon}</span>{group.label}</button>;
                }
                return <NavDropdown key={group.label} label={group.label} icon={group.icon} items={group.items} activePage={activePage} userAccess={userAccess} onNav={onNav} accentColor={group.color} compact={compact} />;
              })}
            </div>
          </>}
        </div>
        {/* RIGHT GROUP: Admin · [name badge — full only] · Refresh · Revert · SyncStatus · Logout */}
        <div style={{ display: "flex", alignItems: "center", gap: mobile ? 6 : 12 }}>
          {!mobile && <>
            <div className="desktop-actions" style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 12 }}>
              {/* Admin button: full padding in Tier A, icon-only in Tier B */}
              {isAdmin(user.email) && (
                <button onClick={onAdmin} title="Admin panel"
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: compact ? "8px 10px" : "8px 20px", borderRadius: 10, background: "linear-gradient(135deg, #DC2626 0%, #F43F5E 100%)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 700, boxShadow: "0 4px 16px rgba(239,68,68,0.4)" }}>⚙️{!compact && " Admin"}</button>
              )}
              {/* F1-v2: name badge only in full Tier A (>= 1440px) */}
              {!compact && (
                <div style={{ padding: "5px 16px", borderRadius: 20, background: "rgba(99,102,241,0.15)", border: "1.5px solid rgba(99,102,241,0.4)", fontSize: 13, color: "#A5B4FC", fontWeight: 600, letterSpacing: "0.2px" }}>{user.name}</div>
              )}
              <RefreshButton />
              {/* Revert: icon-only at all widths — ↩ is self-explanatory; title for accessibility */}
              <button onClick={()=>window.dispatchEvent(new CustomEvent('blitz-revert'))} title="Revert last change"
                style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(239,68,68,0.12)", border:"1.5px solid rgba(239,68,68,0.35)", color:"#F87171", cursor:"pointer", fontSize:16, fontWeight:700, padding:"6px 8px", borderRadius:8, transition:"all 0.15s" }}
                onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,68,68,0.22)";}} onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,68,68,0.12)"}}
              >↩</button>
              <SyncStatus compact={compact} />
              {/* F1-v2: Logout icon-only in Tier B; keep aria-label + title */}
              <button onClick={onLogout} title="Logout" aria-label="Logout"
                style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 13, fontWeight: 500, padding: "6px 8px", borderRadius: 8 }}
                onMouseEnter={e => e.currentTarget.style.color = "#F87171"} onMouseLeave={e => e.currentTarget.style.color = "#64748B"}
              ><LogOut size={16} strokeWidth={2} />{!compact && <span>Logout</span>}</button>
            </div>
          </>}
          {mobile && <>
            <SyncStatus />
            <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "1px solid #E2E8F0", color: "#334155", cursor: "pointer", padding: "6px 10px", borderRadius: 8, fontSize: 18 }}>☰</button>
          </>}
        </div>
      </header>
      {mobile && menuOpen && (
        <MobileNav pages={allNavPages} current={activePage} userAccess={userAccess} onNav={onNav} onLogout={onLogout} onClose={() => setMenuOpen(false)} />
      )}
    </>
  );
}
